import { Flex, Select, Spin, Typography } from 'antd'
import PropTypes from 'prop-types'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CloseCircleFilled } from "@ant-design/icons";


import useRecentSearchPlaces from '@/hooks/useRecentSearchPlaces';
import useDebounce from '@/context/useDebounce';
import queryConfig from '@/config/queryConfig';
import Openstreetmap from '@/services/Openstreetmap';
import { colors } from '@/utils/antdTheme';
import useTranslation from '@/hooks/useTranslation';
import { getCityFromAddress, getCountryFromAddress, getStateFromAddress } from '@/utils/openstreetUtils';
import { CountryStateCity } from '@/utils/countryStateCitySearch';


const LABEL      = "Place";
const EMPTY_LIST = [];
const DEFAULT_FILTER = {
    page: 1,
    search: "",
    limit: 20,
};

const DEFAULT_TYPE = {
    COUNTRY: "country",
    STATE: "state",
    CITY: "city",
    PLACE: "place",
}

const makeKey = (parts) =>
    parts.filter(Boolean).join("-").replaceAll(" ", "-").toLowerCase();

// Deduplicate by key — first item wins, so tansuasici results (added first) take priority over OSM
const dedupeByKey = (items) => {
    const seen = new Map();
    for (const item of items) {
        if (!seen.has(item.key)) seen.set(item.key, item);
    }
    return [...seen.values()];
};

export const toCountryOption = (country) => {
    const key = makeKey([DEFAULT_TYPE.COUNTRY, country?.name]);
    return {
        key, value: key,
        label: country?.name,
        searchType: DEFAULT_TYPE.COUNTRY,
        searchBy: "tansuasici",
        place: {
            countryCode: country?.isoCode,
            country: country?.name,
            latitude: country?.latitude,
            longitude: country?.longitude,
            location: [country?.name].filter(Boolean),
        },
        data: { ...country },
    };
};

export const toStateOption = (state) => {
    const countryName = state?.countryName;
    const stateName   = state?.name;
    const placeArray  = [stateName, countryName].filter(Boolean);
    const key = makeKey([DEFAULT_TYPE.STATE, ...placeArray]);
    return {
        key, value: key,
        label: stateName,
        searchType: DEFAULT_TYPE.STATE,
        searchBy: "tansuasici",
        place: {
            countryCode: state?.countryCode,
            stateCode: state?.stateCode,
            country: countryName,
            state: stateName,
            latitude: state?.latitude,
            longitude: state?.longitude,
            display_name: placeArray.join(", "),
            location: placeArray,
        },
        data: { ...state },
    };
};

export const toCityOption = (city) => {
    const countryName = city?.countryName;
    const stateName   = city?.stateName;
    const cityName    = city?.name;
    const placeArray  = [cityName, stateName, countryName].filter(Boolean);
    const key = makeKey([DEFAULT_TYPE.CITY, ...placeArray]);
    return {
        key,
        value: key,
        label: cityName,
        searchType: DEFAULT_TYPE.CITY,
        searchBy: "tansuasici",
        place: {
            countryCode: city?.countryCode,
            stateCode: city?.stateCode,
            country: countryName,
            state: stateName,
            city: cityName,
            latitude: city?.latitude,
            longitude: city?.longitude,
            display_name: placeArray.join(", "),
            location: placeArray,
        },
        data: { ...city },
    };
};

export const toOsmOption = (item) => {
    const display_name = item?.display_name;
    const countryName  = getCountryFromAddress(item?.address);
    const stateName    = getStateFromAddress(item?.address);
    const cityName     = getCityFromAddress(item?.address);
    let searchType;
    if (cityName)         searchType = DEFAULT_TYPE.CITY;
    else if (stateName)   searchType = DEFAULT_TYPE.STATE;
    else if (countryName) searchType = DEFAULT_TYPE.COUNTRY;
    else                  searchType = DEFAULT_TYPE.PLACE;

    const key = makeKey([searchType, ...(display_name?.replaceAll(", ",",")?.split(',') ?? [])]);
    const stateKey = (item) => {
        const address = item?.address ?? {};

        const isoEntry = Object.entries(address).find(([key]) => key.includes("ISO3166"));

        if (!isoEntry) return null;

        const [, isoValue] = isoEntry;

        return isoValue?.split("-").at(-1) ?? null;
    };
    return {
        key,
        value: key,
        label: item?.name || item?.display_name || cityName || stateName || countryName || "",
        searchType,
        searchBy: "openstreetmap",
        place: {
            countryCode: item?.address?.country_code,
            stateCode: stateKey(item),
            country: countryName,
            state: stateName,
            city: cityName,
            latitude: item?.lat,
            longitude: item?.lon,
            display_name,
            location: [cityName, stateName, countryName].filter(Boolean),
        },
        data: { ...item },
    };
};

export const findOptionByCountryStateCity = async ({country=null,state=null,city=null}) => {
    let option = null;
    if(city && state && country) {
        const found = CountryStateCity.searchCities(city)?.find(c => c.name.toLowerCase() === city.toLowerCase() && c.stateName.toLowerCase() === state.toLowerCase() && c.countryName.toLowerCase() === country.toLowerCase());
        if(found){
            option = toCityOption(found);
        }
    }else if(state && country){
        const found = CountryStateCity.searchStates(state)?.find(s => s.name.toLowerCase() === state.toLowerCase() && s.countryName.toLowerCase() === country.toLowerCase());
        if(found){
            option = toStateOption(found);
        }
    }else if(country){
        const found = CountryStateCity.searchCountries(country)?.find(c => c.name.toLowerCase() === country.toLowerCase());
        if(found){
            option = toCountryOption(found);
        }
    }
    
    if(!option){
        const filterLocation = [city,state,country].filter(Boolean).join(",")
        let response = await Openstreetmap.search({
            q: filterLocation,
            limit: 1,
            addressdetails: 1,
        })
        if(response?.length > 0){
            option = toOsmOption(response[0]);
        }
    }
    return option;

}

const OptionItem = ({ option }) => {
    const { t } = useTranslation();
    const { key, label, searchType, place } = option.data || {};

    if (searchType === DEFAULT_TYPE.COUNTRY) {
        return (
            <Typography.Paragraph key={key} style={{ marginBottom: 0 }}>
                {t(label)}
            </Typography.Paragraph>
        );
    }

    return (
        <Flex key={key} gap={0} style={{ marginBottom: 0 }} vertical>
            <Typography.Paragraph strong style={{ marginBottom: 0 }}>
                {t(label)}
            </Typography.Paragraph>
            {place?.display_name?.length > 0 && (
                <Typography.Paragraph style={{ marginBottom: 0, textWrap: "auto", fontSize: "small" }}>
                    {t(place.display_name)}
                </Typography.Paragraph>
            )}
        </Flex>
    );
};

OptionItem.propTypes = {
    option: PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        searchType: PropTypes.string,
        place: PropTypes.object,
        data: PropTypes.object,
    }),
};

const selectProps = {
    allowClear: true,
    placeholder: `Select ${LABEL}`,
    getPopupContainer: (trigger) => (trigger?.parentElement || document.body),
}

const AsyncPlaceSelect = ({
    filter: filterProp = {},
    onChange: onChangeParent,
    filterByCountry = false,
    filterByState = false,
    filterByCity = false,
    ...asyncProp
}) => {
    const { t } = useTranslation();
    const setGlobalStateSmart = useRecentSearchPlaces(s => s.addPlace);
    const globalList          = useRecentSearchPlaces(s => s.places ?? EMPTY_LIST);
    const [filter, setFilter] = useState(DEFAULT_FILTER);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(null);
    const debouncedFilter = useDebounce(filter || "");
    const skipNextSearchRef = useRef(false);

    const filterOption = useCallback((input, option) => {
        return option?.label?.toLowerCase()?.includes(input.toLowerCase()) ?? false;
    }, []);

    const { data: responsePlaces = [], isFetching } = useQuery({
        queryKey: [queryConfig.RECENT_SEARCH_PLACES, debouncedFilter],
        queryFn: async () => {
            const tansuasiciResults = [
                ...CountryStateCity.searchCountries(debouncedFilter?.search)?.map(toCountryOption) ?? [],
                ...CountryStateCity.searchStates(debouncedFilter?.search)?.map(toStateOption) ?? [],
                ...CountryStateCity.searchCities(debouncedFilter?.search)?.map(toCityOption) ?? [],
            ];

            const rawResponse = await Openstreetmap.search({
                q: debouncedFilter?.search,
                limit: debouncedFilter?.limit,
                addressdetails: 1,
                ...filterProp,
            });
            const osmResults = Array.isArray(rawResponse) ? rawResponse.map(toOsmOption) : [];

            // tansuasici results are preferred; OSM fills in what tansuasici doesn't have
            return dedupeByKey([...tansuasiciResults, ...osmResults]);
        },
        enabled: Boolean(debouncedFilter?.search),
        staleTime: 60 * 1000,
    });

    useEffect(() => {
        setFilter({ ...DEFAULT_FILTER, ...filterProp });
    }, [JSON.stringify(filterProp)]);

    const renderOption = useCallback(
        (option) => (
            <OptionItem
                option={option}
                t={t}
                filterByCountry={filterByCountry}
                filterByState={filterByState}
                filterByCity={filterByCity}
            />
        ),
        [t, filterByCountry, filterByState, filterByCity]
    );

    const onSearch = useCallback((search = "") => {
        if (search?.trim() === "") return;
        setFilter(prev => ({ ...prev, page: 1, search }));
    }, []);

    const onClear = useCallback(() => {
        setFilter(prev => ({ ...prev, page: 1, search: "" }));
        setSelectedValue(null);
        setDropdownOpen(false);
        onChangeParent?.(null, null);
    }, [onChangeParent]);

    const onChange = useCallback((value, option) => {
        if (!value) {
            onClear();
            return;
        }
        setSelectedValue(value);
        if (option) setGlobalStateSmart(option, "key");
        onChangeParent?.(value, option ?? null);
    }, [onClear, setGlobalStateSmart, onChangeParent]);

    const options = useMemo(() => {
        let lists = filter.search ? responsePlaces : globalList;
        if (filterByCountry) lists = lists?.filter(f => f.searchType === DEFAULT_TYPE.COUNTRY);
        if (filterByState)   lists = lists?.filter(f => f.searchType === DEFAULT_TYPE.STATE);
        if (filterByCity)    lists = lists?.filter(f => f.searchType === DEFAULT_TYPE.CITY);
        if(filterProp?.countrycodes) lists = lists?.filter(f => f?.place?.countryCode && filterProp?.countrycodes?.map(c => c?.toLowerCase()).includes(f?.place?.countryCode?.toLowerCase()));
        return lists?.filter(f=>f?.label?.length>0) || [];
    }, [responsePlaces, globalList, filter.search, filterByCountry, filterByState, filterByCity, filterProp]);

    return (
        <Select
            open={dropdownOpen}
            onOpenChange={(open) => {
                setDropdownOpen(open);
                if (open) skipNextSearchRef.current = true;
            }}
            onClick={() => setDropdownOpen(true)}
            onFocus={() => setDropdownOpen(true)}
            style={{ width: "100%", ...asyncProp.style }}
            styles={{
                popup: {
                    root: { padding: 0 },
                    listItem: {
                        marginInline: 5,
                        paddingInline: 5,
                        paddingBottom: 0,
                        borderBottom: `1px solid ${colors.antPrimary}`,
                        borderRadius: `0px`,
                    }
                },
                ...asyncProp.styles,
            }}
            value={selectedValue ?? undefined}
            {...selectProps}
            {...asyncProp}
            showSearch={{ filterOption, onSearch }}
            allowClear={{ clearIcon: <CloseCircleFilled onClick={onClear} /> }}
            onClear={onClear}
            onChange={onChange}
            options={options}
            notFoundContent={isFetching ? <Spin size="small" /> : t('map_search.no_results')}
            getPopupContainer={() => document.body}
            optionRender={renderOption}
        />
    );
};

AsyncPlaceSelect.propTypes = {
    filterByCountry: PropTypes.bool,
    filterByState: PropTypes.bool,
    filterByCity: PropTypes.bool,
    filter: PropTypes.object,
    onChange: PropTypes.func,
    style: PropTypes.object,
}

export default memo(AsyncPlaceSelect)
